import {useCallback, useMemo, useRef, useState} from 'react';
import {NetworkStatus, useQuery} from '@apollo/client';
import {ViewerCanvasesQuery} from '~/domains/viewer/graphql';
import {useAuth} from '~/lib/auth';
import {CanvasPanelHeader} from '../canvas-panel';
import {PagesList} from './pages-list';
import type {PageListItem, PageSection} from './utils';
import {
  GROUPING_THRESHOLD,
  groupPagesByRecency,
  singleAdvisorName,
  sortPagesNewestFirst,
} from './utils';

interface Props {
  onClose: () => void;
}

const PAGE_SIZE = 50;

function subtitleFor(
  count: number,
  advisor: string | null,
  settled: boolean,
): string {
  if (!settled) {
    return 'Everything shared with you at this firm.';
  }
  const noun = count === 1 ? 'page' : 'pages';
  const by = advisor ? ` by ${advisor}` : '';
  return `${count} ${noun} shared with you${by}.`;
}

export const PagesView = ({onClose}: Props) => {
  const {identifier} = useAuth();

  // cache-first: reopening the view reads the warm cache instead of refetching.
  const {data, loading, error, refetch, fetchMore, networkStatus} = useQuery(
    ViewerCanvasesQuery,
    {
      variables: {first: PAGE_SIZE},
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
    },
  );

  const connection = data?.viewer.canvases;
  const hasNextPage = connection?.pageInfo.hasNextPage ?? false;
  const endCursor = connection?.pageInfo.endCursor ?? null;

  const items = useMemo<PageListItem[]>(() => {
    // One row per grant server-side; collapse to distinct pages.
    const seen = new Set<string>();
    const rows: PageListItem[] = [];
    for (const edge of connection?.edges ?? []) {
      const canvas = edge.node;
      if (!canvas || seen.has(canvas.identifier)) {
        continue;
      }
      seen.add(canvas.identifier);
      rows.push({
        identifier: canvas.identifier,
        title: canvas.title ?? null,
        sharedAt: canvas.sharedAt,
        advisorName: canvas.advisor?.name ?? null,
      });
    }
    return sortPagesNewestFirst(rows);
  }, [connection?.edges]);

  const advisor = useMemo(() => singleAdvisorName(items), [items]);
  const showAdvisor =
    advisor === null && items.some((item) => item.advisorName !== null);

  const sections = useMemo<PageSection[]>(
    () =>
      items.length >= GROUPING_THRESHOLD
        ? groupPagesByRecency(items)
        : [{key: 'all', label: null, items}],
    [items],
  );

  // Scrolling drives fetchMore, so a failure must stop the sentinel rather
  // than retry on every scroll event.
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);

  // One at a time: a re-triggered sentinel must not request the same cursor.
  const fetchMoreInFlight = useRef(false);
  const loadMore = useCallback(() => {
    if (!hasNextPage || !endCursor || fetchMoreInFlight.current) {
      return;
    }
    fetchMoreInFlight.current = true;
    setLoadMoreFailed(false);
    fetchMore({variables: {after: endCursor}})
      .catch(() => setLoadMoreFailed(true))
      .finally(() => {
        fetchMoreInFlight.current = false;
      });
  }, [endCursor, fetchMore, hasNextPage]);

  // Skeleton on first load only; cached rows outrank a failed revalidation.
  const showSkeleton = !data && loading;
  const showError = !!error && !data;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  // totalCount covers pages not loaded yet; once loaded, the deduped row count
  // is what the reader can actually see.
  const count = hasNextPage ? (connection?.totalCount ?? 0) : items.length;

  return (
    <>
      <CanvasPanelHeader
        title="Your pages"
        subtitle={subtitleFor(count, advisor, !showSkeleton && !showError)}
        onClose={onClose}
        closeLabel="Close pages"
      />
      <div className="min-h-0 flex-1 overflow-hidden">
        <PagesList
          sections={sections}
          showAdvisor={showAdvisor}
          currentIdentifier={identifier}
          loading={showSkeleton}
          error={showError}
          hasNextPage={hasNextPage}
          isFetchingMore={isFetchingMore}
          loadMoreFailed={loadMoreFailed}
          onLoadMore={loadMore}
          onRetry={() => void refetch()}
          onNavigate={onClose}
        />
      </div>
    </>
  );
};
